import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trophy,
  AlertCircle,
  Users,
  Target,
  FileText,
  X,
  SlidersHorizontal,
  Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveBadgeUrl = (badge) => {
  if (!badge) return null;
  if (badge.startsWith('http://') || badge.startsWith('https://')) return badge;
  return `${BACKEND_URL}${badge}`;
};

export default function Matches() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedMatchId, setExpandedMatchId] = useState(null);
  const [opponentBadgeMap, setOpponentBadgeMap] = useState({});
  const showOpponentBadges = user?.show_opponent_badges !== false;

  // Format date as "Sun, Mar 15"
  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${weekday}, ${month} ${day}`;
  };

  const toggleExpandMatch = (matchId) => {
    setExpandedMatchId(expandedMatchId === matchId ? null : matchId);
  };

  useEffect(() => {
    if (user?.current_season_id) {
      fetchMatches();
    } else {
      setLoading(false);
    }
  }, [user?.current_season_id]);

  useEffect(() => {
    if (!showOpponentBadges) {
      setOpponentBadgeMap({});
      return;
    }
    const fetchOpponents = async () => {
      try {
        const res = await axios.get(`${API}/opponents`);
        const map = {};
        (res.data || []).forEach((o) => {
          if (o?.name) map[o.name.toLowerCase().trim()] = o.badge || null;
        });
        setOpponentBadgeMap(map);
      } catch (error) {
        // Silent fail — badges will just show placeholders
      }
    };
    fetchOpponents();
  }, [showOpponentBadges]);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`);
      setMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to fetch matches');
    } finally {
      setLoading(false);
    }
  };

  // Get match result considering ET and penalties for cup matches
  const getMatchResult = (match) => {
    if (match.is_cup) {
      if (match.our_score_pens != null && match.opponent_score_pens != null) {
        if (match.our_score_pens > match.opponent_score_pens) return { label: 'W', color: 'bg-emerald-500 text-white' };
        if (match.our_score_pens < match.opponent_score_pens) return { label: 'L', color: 'bg-red-500 text-white' };
      }
      if (match.our_score_et != null && match.opponent_score_et != null) {
        if (match.our_score_et > match.opponent_score_et) return { label: 'W', color: 'bg-emerald-500 text-white' };
        if (match.our_score_et < match.opponent_score_et) return { label: 'L', color: 'bg-red-500 text-white' };
      }
    }
    if (match.our_score > match.opponent_score) return { label: 'W', color: 'bg-emerald-500 text-white' };
    if (match.our_score < match.opponent_score) return { label: 'L', color: 'bg-red-500 text-white' };
    return { label: 'D', color: 'bg-amber-500 text-white' };
  };

  const filteredMatches = matches
    .filter(match => {
      const matchesSearch = match.opponent.toLowerCase().includes(searchQuery.toLowerCase()) || 
                           (match.competition || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || match.status === statusFilter;
      const matchesType = typeFilter === 'all' || 
                          (typeFilter === 'league' && !match.is_cup) || 
                          (typeFilter === 'cup' && match.is_cup);
      
      let matchesResult = resultFilter === 'all';
      if (!matchesResult && match.status === 'result') {
        const result = getMatchResult(match);
        if (resultFilter === 'wins' && result.label === 'W') matchesResult = true;
        if (resultFilter === 'losses' && result.label === 'L') matchesResult = true;
        if (resultFilter === 'draws' && result.label === 'D') matchesResult = true;
      }
      
      return matchesSearch && matchesStatus && matchesType && matchesResult;
    })
    .sort((a, b) => {
      const dateTimeA = new Date(`${a.date}T${a.kick_off_time || '00:00'}`);
      const dateTimeB = new Date(`${b.date}T${b.kick_off_time || '00:00'}`);
      return dateTimeB - dateTimeA;
    });

  if (!user?.current_season_id) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="no-season-message">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">No Season Selected</h2>
          <p className="text-gray-500 mb-6">Create a season first to manage matches.</p>
          <Button onClick={() => navigate('/seasons')} className="btn-primary rounded-xl">Go to Seasons</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="matches-container">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">Matches</h1>
          {isAdmin && (
            <Button 
              variant="outline"
              size="icon"
              onClick={() => navigate('/matches/new')}
              className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
              data-testid="add-match-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Search matches..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="pl-9 pr-9 rounded-xl" 
                data-testid="search-input" 
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')} 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="clear-search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(prev => !prev)}
              className={`rounded-xl shrink-0 ${showFilters || typeFilter !== 'all' || resultFilter !== 'all' || statusFilter !== 'all' ? 'bg-emerald-50 border-emerald-300 text-emerald-600' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'}`}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 md:flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="rounded-xl text-xs sm:text-sm md:w-[110px]" data-testid="type-filter-select">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="league">League</SelectItem>
                  <SelectItem value="cup">Cup</SelectItem>
                </SelectContent>
              </Select>
              <Select value={resultFilter} onValueChange={setResultFilter}>
                <SelectTrigger className="rounded-xl text-xs sm:text-sm md:w-[110px]" data-testid="result-filter-select">
                  <SelectValue placeholder="All Results" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Results</SelectItem>
                  <SelectItem value="wins">Wins</SelectItem>
                  <SelectItem value="draws">Draws</SelectItem>
                  <SelectItem value="losses">Losses</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="rounded-xl text-xs sm:text-sm md:w-[120px]" data-testid="status-filter-select">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="result">Results</SelectItem>
                  <SelectItem value="postponed">Postponed</SelectItem>
                  <SelectItem value="abandoned">Abandoned</SelectItem>
                </SelectContent>
              </Select>
              {(typeFilter !== 'all' || resultFilter !== 'all' || statusFilter !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-gray-500 hover:text-red-500 rounded-xl px-3"
                  onClick={() => { setTypeFilter('all'); setResultFilter('all'); setStatusFilter('all'); }}
                  data-testid="reset-filters"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Reset
                </Button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 mt-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : filteredMatches.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-300" strokeWidth={1.5} />
            <p className="text-gray-500">No matches found</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 mt-4">
            {filteredMatches.map((match) => {
              const venueAbbrev = match.venue === 'home' ? 'H' : match.venue === 'away' ? 'A' : 'N';
              const result = match.status === 'result' ? getMatchResult(match) : null;
              const opponentBadge = resolveBadgeUrl(opponentBadgeMap[(match.opponent || '').toLowerCase().trim()]);

              const getStatusChip = () => {
                if (match.status === 'result') return { label: result.label, color: result.color };
                if (match.status === 'postponed') return { label: 'P', color: 'bg-gray-800 text-white' };
                if (match.status === 'abandoned') return { label: 'A', color: 'bg-gray-800 text-white' };
                return null;
              };
              const statusChip = getStatusChip();
              const finalScoreLabel = match.is_cup && match.our_score_pens != null && match.opponent_score_pens != null
                ? `PEN (${match.our_score_pens}-${match.opponent_score_pens})`
                : match.is_cup && match.our_score_et != null && match.opponent_score_et != null
                ? 'AET'
                : 'FT';

              return (
                <div
                  key={match.id}
                  style={{ border: '1px solid #e5e2dc', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
                  className="rounded-2xl cursor-pointer transition-all px-3 py-3 sm:px-5 sm:py-4 hover:shadow-sm"
                  onClick={() => navigate(`/matches/${match.id}/edit`)}
                  data-testid={`match-card-${match.id}`}
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Left: Opponent Badge (only when enabled) */}
                    {showOpponentBadges && (
                      opponentBadge ? (
                        <img
                          src={opponentBadge}
                          alt={`${match.opponent} badge`}
                          className="w-11 h-11 sm:w-14 sm:h-14 object-contain shrink-0"
                          data-testid={`match-opponent-badge-${match.id}`}
                        />
                      ) : (
                        <div
                          className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0"
                          data-testid={`match-opponent-badge-${match.id}`}
                        >
                          <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" strokeWidth={1.5} />
                        </div>
                      )
                    )}

                    {/* Middle: Opponent & Competition */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="font-bold text-gray-900 text-base sm:text-lg truncate leading-tight">
                        {match.opponent}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500 truncate mt-0.5">
                        {match.competition ? `${match.competition} (${venueAbbrev})` : `(${venueAbbrev})`}
                      </p>
                    </div>

                    {/* Right: Score/Time */}
                    <div className="text-right shrink-0 ml-1 sm:ml-2">
                      {match.status === 'result' ? (
                        <>
                          <p className="text-lg sm:text-xl font-bold text-gray-900 whitespace-nowrap tracking-tight">
                            {match.is_cup && match.our_score_et != null && match.opponent_score_et != null
                              ? `${match.our_score_et} - ${match.opponent_score_et}`
                              : `${match.our_score} - ${match.opponent_score}`}
                          </p>
                          <div className="flex items-center justify-end gap-1.5 mt-0.5">
                            <span className="text-xs sm:text-sm text-gray-400">{finalScoreLabel}</span>
                            {statusChip && (
                              <span
                                className={`inline-flex items-center justify-center h-[22px] font-bold ${statusChip.color}`}
                                style={{ fontSize: '8px', minWidth: '18px', paddingLeft: '0.275rem', paddingRight: '0.275rem', borderRadius: 'calc(var(--radius) - 6px)' }}
                                data-testid={`match-result-chip-${match.id}`}
                              >
                                {statusChip.label}
                              </span>
                            )}
                          </div>
                        </>
                      ) : match.status === 'postponed' ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <p className="text-base sm:text-lg font-bold text-gray-400">P - P</p>
                          {statusChip && (
                            <span
                              className={`inline-flex items-center justify-center h-[22px] font-bold ${statusChip.color}`}
                              style={{ fontSize: '8px', minWidth: '18px', paddingLeft: '0.275rem', paddingRight: '0.275rem', borderRadius: 'calc(var(--radius) - 6px)' }}
                            >
                              {statusChip.label}
                            </span>
                          )}
                        </div>
                      ) : match.status === 'abandoned' ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <p className="text-base sm:text-lg font-bold text-gray-400">A - A</p>
                          {statusChip && (
                            <span
                              className={`inline-flex items-center justify-center h-[22px] font-bold ${statusChip.color}`}
                              style={{ fontSize: '8px', minWidth: '18px', paddingLeft: '0.275rem', paddingRight: '0.275rem', borderRadius: 'calc(var(--radius) - 6px)' }}
                            >
                              {statusChip.label}
                            </span>
                          )}
                        </div>
                      ) : (
                        <>
                          <p className="text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                            {formatDateShort(match.date)}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                            {match.kick_off_time || '--:--'}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Expandable Action Buttons */}
                  {/* Admin sees all buttons, Players only see view buttons for results */}
                  {(isAdmin || match.status === 'result') && (
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      expandedMatchId === match.id ? 'max-h-16 opacity-100 mt-2' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        {/* Edit button - Admin only */}
                        {isAdmin && (
                          <Link to={`/matches/${match.id}/edit`} className="flex-1">
                            <Button variant="outline" size="sm" className="w-full rounded-lg h-9" data-testid={`edit-match-${match.id}`}>
                              <Edit2 className="w-4 h-4 sm:mr-1.5" strokeWidth={1.5} />
                              <span className="hidden sm:inline text-xs">Edit</span>
                            </Button>
                          </Link>
                        )}
                        {/* Squad button - Admin for all, Players for results only */}
                        {match.status !== 'postponed' && (isAdmin || match.status === 'result') && (
                          <Link to={`/matches/${match.id}/edit?tab=squad${!isAdmin ? '&view=true' : ''}`} className="flex-1">
                            <Button variant="outline" size="sm" className="w-full rounded-lg h-9" data-testid={`edit-squad-${match.id}`}>
                              <Users className="w-4 h-4 sm:mr-1.5" strokeWidth={1.5} />
                              <span className="hidden sm:inline text-xs">Squad</span>
                            </Button>
                          </Link>
                        )}
                        {/* Events and Stats - visible for results */}
                        {match.status === 'result' && (
                          <>
                            <Link to={`/matches/${match.id}/edit?tab=events${!isAdmin ? '&view=true' : ''}`} className="flex-1">
                              <Button variant="outline" size="sm" className="w-full rounded-lg h-9" data-testid={`events-${match.id}`}>
                                <FileText className="w-4 h-4 sm:mr-1.5" strokeWidth={1.5} />
                                <span className="hidden sm:inline text-xs">Events</span>
                              </Button>
                            </Link>
                            <Link to={`/matches/${match.id}/edit?tab=stats${!isAdmin ? '&view=true' : ''}`} className="flex-1">
                              <Button variant="outline" size="sm" className="w-full rounded-lg h-9" data-testid={`record-stats-${match.id}`}>
                                <Target className="w-4 h-4 sm:mr-1.5" strokeWidth={1.5} />
                                <span className="hidden sm:inline text-xs">Stats</span>
                              </Button>
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
